import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { enquiry } from '../../../../helper/api_url';
import { swal } from '../../../../helper/swal';
import { getUniqueDistricts } from '../EnquiryList/enquiryUtils';

export const useMeetingArrange = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    // Today's date formatted as YYYY-MM-DD
    const today = new Date();
    const todayFormatted = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    // Filters: date is optional (all meetings by default), user/staff filter, district filter, search term
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedUser, setSelectedUser] = useState('all');
    const [selectedDistrict, setSelectedDistrict] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    // Fetch all enquiries with nested followups
    const {
        data: enquiries = [],
        isLoading,
        isError,
        error
    } = useQuery({
        queryKey: ['enquirys'],
        queryFn: enquiry.list,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
        select: (data) => data?.data?.enquiry || []
    });

    useEffect(() => {
        if (isError && error) {
            swal.error(error.response?.data?.message || error.message || 'Failed to fetch meeting arrange records');
        }
    }, [isError, error]);

    // Check the LAST followup of each enquiry; if its status is 'postpond', include it in Meeting Arrange
    const allMeetings = useMemo(() => {
        const meetings = [];
        enquiries.forEach((enq) => {
            let lastFollow = null;
            if (Array.isArray(enq.followups) && enq.followups.length > 0) {
                // Sort by created_at or id to reliably identify the latest follow-up
                const sortedFollows = [...enq.followups].sort((a, b) => {
                    const timeA = new Date(a.created_at || 0).getTime();
                    const timeB = new Date(b.created_at || 0).getTime();
                    return timeA - timeB || (a.id || 0) - (b.id || 0);
                });
                lastFollow = sortedFollows[sortedFollows.length - 1];
            }

            // Effective status is determined by the last follow-up, or enquiry itself if no followups exist
            const effectiveStatus = (lastFollow ? lastFollow.status : enq.status || '').toLowerCase();

            if (effectiveStatus === 'postpond') {
                const target = lastFollow || enq;
                const createdDateStr = target.created_at ? target.created_at.slice(0, 10) : '';
                meetings.push({
                    ...target,
                    meetingId: target.id,
                    enquiry: enq,
                    meetingDate: target.joining_date || '',
                    callDate: createdDateStr,
                    remark: target.remark || '',
                    status: 'postpond',
                    callerUser: target.created_by || target.user || enq.updated_by || enq.created_by
                });
            }
        });
        return meetings.sort((a, b) => new Date(b.created_at || b.meetingDate) - new Date(a.created_at || a.meetingDate));
    }, [enquiries]);

    // Unique list of users/staff available across enquiries and meetings
    const userList = useMemo(() => {
        const map = new Map();
        // First add users who made meeting follow-ups
        allMeetings.forEach((log) => {
            const user = log.callerUser;
            if (user && user.id) {
                const name = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || `Staff #${user.id}`;
                if (!map.has(user.id)) {
                    map.set(user.id, { id: user.id, name });
                }
            }
        });
        // Also ensure all staff members from enquiries are present in the filter dropdown
        enquiries.forEach((enq) => {
            [enq.updated_by, enq.created_by].forEach((user) => {
                if (user && user.id) {
                    const name = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || `Staff #${user.id}`;
                    if (!map.has(user.id)) {
                        map.set(user.id, { id: user.id, name });
                    }
                }
            });
        });
        return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [allMeetings, enquiries]);

    // Unique sorted districts
    const districtList = useMemo(() => {
        return getUniqueDistricts(allMeetings);
    }, [allMeetings]);

    // Summary statistics
    const stats = useMemo(() => {
        return {
            total: allMeetings.length,
            today: allMeetings.filter((m) => m.meetingDate === todayFormatted).length,
            upcoming: allMeetings.filter((m) => m.meetingDate && m.meetingDate > todayFormatted).length
        };
    }, [allMeetings, todayFormatted]);

    // Filtered meetings according to user, district, date, and search term
    const filteredMeetings = useMemo(() => {
        return allMeetings.filter((item) => {
            // Optional Date Filter (matches scheduled meeting date or entry call date)
            if (selectedDate) {
                const meetDate = String(item.meetingDate || '').slice(0, 10);
                const callDate = String(item.callDate || '').slice(0, 10);
                if (meetDate !== selectedDate && callDate !== selectedDate) return false;
            }

            // User / Staff Filter (User requested: "follow kis user ne kiya hai postpond wala")
            if (selectedUser !== 'all' && String(item.callerUser?.id) !== String(selectedUser)) {
                return false;
            }

            // District Filter
            if (selectedDistrict !== 'all') {
                const itemDistrict = (item.enquiry?.district || '').trim().toLowerCase();
                if (itemDistrict !== selectedDistrict.toLowerCase()) return false;
            }

            // Search Term Filter
            if (searchTerm.trim()) {
                const term = searchTerm.toLowerCase();
                const schoolName = (item.enquiry?.name || '').toLowerCase();
                const phone = item.enquiry?.phone || '';
                const principal = (item.enquiry?.father_name || '').toLowerCase();
                const remark = (item.remark || '').toLowerCase();
                const caller = `${item.callerUser?.first_name || ''} ${item.callerUser?.last_name || ''}`.toLowerCase();
                const district = (item.enquiry?.district || '').toLowerCase();

                return (
                    schoolName.includes(term) ||
                    phone.includes(term) ||
                    principal.includes(term) ||
                    remark.includes(term) ||
                    caller.includes(term) ||
                    district.includes(term)
                );
            }

            return true;
        });
    }, [allMeetings, selectedDate, selectedUser, selectedDistrict, searchTerm]);

    const handleFollowSuccess = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['enquirys'] });
    }, [queryClient]);

    const handleReport = useCallback((row) => {
        const enq = row.enquiry || row;
        const reportData = {
            ...enq,
            followups: enq.followups && enq.followups.length > 0 ? enq.followups : [row]
        };
        navigate('/report', { state: { data: reportData } });
    }, [navigate]);

    return {
        allMeetings,
        filteredMeetings,
        isLoading,
        selectedDate,
        setSelectedDate,
        todayFormatted,
        selectedUser,
        setSelectedUser,
        userList,
        selectedDistrict,
        setSelectedDistrict,
        districtList,
        searchTerm,
        setSearchTerm,
        stats,
        handleFollowSuccess,
        handleReport
    };
};

export default useMeetingArrange;
